// Brand marks for the platform a review was posted on.
//
// Official logos are loaded from /brand/platforms/<id>.svg (drop the official
// asset files there — see public/brand/platforms/README.md). Until an official
// file is present, we fall back to a lightweight inline mark so the UI never
// breaks. Full-colour official artwork should not be hand-recreated in code;
// this component references the real asset files instead.

import { useState } from "react";

export const REVIEW_PLATFORMS = [
  { id: "linkedin", label: "LinkedIn", color: "#0A66C2" },
  { id: "google", label: "Google", color: "#4285F4" },
  { id: "trustpilot", label: "Trustpilot", color: "#00B67A" },
  { id: "glassdoor", label: "Glassdoor", color: "#0CAA41" },
  { id: "justdial", label: "JustDial", color: "#F58220" },
  { id: "ambitionbox", label: "AmbitionBox", color: "#1E4C8A" },
];

// Public paths to the official logo assets (square icon marks work best in the
// compact review chip). Provide these files to switch from the fallbacks.
//
// Trustpilot deliberately has no entry here: the supplied trustpilot.jpg is a
// cropped star on a white box that renders unclearly in the compact chip, so
// we use the crisp built-in star mark below instead. Drop a clean, trimmed
// asset and re-add the path here to switch back to an official file.
const OFFICIAL_LOGOS = {
  linkedin: "/brand/platforms/linkedin.png",
  google: "/brand/platforms/google.png",
  glassdoor: "/brand/platforms/glassdoor.png",
  justdial: "/brand/platforms/justdial.png",
  ambitionbox: "/brand/platforms/ambitionbox.png",
};

export function platformMeta(id) {
  return REVIEW_PLATFORMS.find((p) => p.id === id) || null;
}

function LinkedInMark({ size }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden focusable="false">
      <rect width="24" height="24" rx="4" fill="#0A66C2" />
      <path
        fill="#fff"
        d="M7.2 9.5H4.6V19h2.6V9.5ZM5.9 5.2A1.5 1.5 0 1 0 5.9 8.2 1.5 1.5 0 0 0 5.9 5.2ZM19.4 19h-2.6v-4.6c0-1.1 0-2.5-1.5-2.5s-1.8 1.2-1.8 2.4V19h-2.6V9.5h2.5v1.3h.03c.35-.66 1.2-1.35 2.5-1.35 2.66 0 3.15 1.75 3.15 4V19Z"
      />
    </svg>
  );
}

function GoogleMark({ size }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden focusable="false">
      <path fill="#4285F4" d="M23 12.3c0-.8-.1-1.5-.2-2.3H12v4.5h6.2a5.3 5.3 0 0 1-2.3 3.5v2.9h3.7C21.8 19 23 15.9 23 12.3Z" />
      <path fill="#34A853" d="M12 24c3.1 0 5.7-1 7.6-2.8l-3.7-2.9c-1 .7-2.3 1.1-3.9 1.1-3 0-5.5-2-6.4-4.8H1.7v3A11.5 11.5 0 0 0 12 24Z" />
      <path fill="#FBBC05" d="M5.6 14.6a6.9 6.9 0 0 1 0-4.4v-3H1.7a11.5 11.5 0 0 0 0 10.4l3.9-3Z" />
      <path fill="#EA4335" d="M12 4.8c1.7 0 3.2.6 4.4 1.7l3.3-3.3A11.5 11.5 0 0 0 1.7 7.2l3.9 3C6.5 6.8 9 4.8 12 4.8Z" />
    </svg>
  );
}

function TrustpilotMark({ size }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden focusable="false">
      <path
        fill="#00B67A"
        d="M12 2l2.9 6.3 6.9.6-5.2 4.6 1.6 6.8L12 17.8 5.8 20.9l1.6-6.8L2.2 8.9l6.9-.6L12 2Z"
      />
    </svg>
  );
}

function AmbitionBoxMark({ size }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden focusable="false">
      <rect width="24" height="24" rx="4" fill="#1E4C8A" />
      <text
        x="12"
        y="16"
        textAnchor="middle"
        fontSize="9.5"
        fontWeight="700"
        fill="#fff"
        fontFamily="Arial, Helvetica, sans-serif"
      >
        AB
      </text>
    </svg>
  );
}

function GlassdoorMark({ size }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden focusable="false">
      <rect width="24" height="24" rx="4" fill="#0CAA41" />
      <text
        x="12"
        y="16.5"
        textAnchor="middle"
        fontSize="12"
        fontWeight="700"
        fill="#fff"
        fontFamily="Arial, Helvetica, sans-serif"
      >
        G
      </text>
    </svg>
  );
}

function JustDialMark({ size }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden focusable="false">
      <rect width="24" height="24" rx="4" fill="#F58220" />
      <text
        x="12"
        y="16"
        textAnchor="middle"
        fontSize="9.5"
        fontWeight="700"
        fill="#fff"
        fontFamily="Arial, Helvetica, sans-serif"
      >
        Jd
      </text>
    </svg>
  );
}

const FALLBACK_MARKS = {
  linkedin: LinkedInMark,
  google: GoogleMark,
  trustpilot: TrustpilotMark,
  glassdoor: GlassdoorMark,
  justdial: JustDialMark,
  ambitionbox: AmbitionBoxMark,
};

export default function PlatformLogo({ platform, size = 16 }) {
  const [errored, setErrored] = useState(false);
  if (!platform) return null;

  const src = OFFICIAL_LOGOS[platform];
  const Fallback = FALLBACK_MARKS[platform];

  // Use the official asset when available and it loads; otherwise fall back.
  if (src && !errored) {
    const meta = platformMeta(platform);
    return (
      <img
        src={src}
        alt={meta ? `${meta.label} logo` : ""}
        style={{ height: size, width: "auto", maxWidth: size * 1.6 }}
        className="object-contain"
        loading="lazy"
        onError={() => setErrored(true)}
      />
    );
  }

  return Fallback ? <Fallback size={size} /> : null;
}
